import React, { useEffect, useState, useCallback } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, EyeOff, Upload, Loader2, X } from "lucide-react";

const inputCls = "w-full rounded-md border border-[#553204]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#F49E1A]";
const labelCls = "text-xs font-semibold text-[#553204] mb-1 block";

function ImageField({ value, onChange, label = "Image" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(mediaUrl(data.url));
      toast.success("Uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-3">
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload →" />
        <label className="shrink-0 cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-[#553204]/20 px-3 py-2 text-xs text-[#553204] bg-white">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-20 rounded object-cover" />}
    </div>
  );
}

function Shell({ title, onAdd, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-[#2A1808]">{title}</h1>
        {onAdd && <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-full btn-orange px-5 py-2.5 text-sm"><Plus size={16} /> New</button>}
      </div>
      {children}
    </div>
  );
}

// ---------------- Destinations ----------------
export function DestinationsManager() {
  const empty = { name: "", region: "", tagline: "", intro: "", hero_image: "", best_time: "", how_to_reach: "", where_to_stay: "", highlights: [], experiences: [], gallery: [], faqs: [], published: true, order: 0 };
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/destinations").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    try {
      if (editing.slug) await api.put(`/destinations/${editing.slug}`, editing);
      else await api.post("/destinations", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (slug) => { if (!window.confirm("Delete destination?")) return; await api.delete(`/destinations/${slug}`); load(); };

  return (
    <Shell title="Destinations" onAdd={() => setEditing({ ...empty })}>
      <div className="space-y-2">
        {items.map((d) => (
          <div key={d.slug} className="flex items-center justify-between rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4" data-testid={`dest-row-${d.slug}`}>
            <div className="flex items-center gap-3">
              {d.hero_image && <img src={d.hero_image} alt="" className="h-10 w-14 rounded object-cover" />}
              <div><p className="font-medium text-[#2A1808]">{d.name}</p><p className="text-xs text-[#8A6B47]">{d.region}</p></div>
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-xs px-2 py-1 rounded-full ${d.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{d.published ? "Live" : "Draft"}</span>
              <button onClick={() => setEditing({ ...empty, ...d })} className="p-2 text-[#8A6B47] hover:text-[#F49E1A]"><Pencil size={15} /></button>
              <button onClick={() => del(d.slug)} className="p-2 text-[#8A6B47] hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} onSave={save} title={editing.slug ? "Edit destination" : "New destination"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name *</label><input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><label className={labelCls}>Region</label><input className={inputCls} value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} /></div>
          </div>
          <div><label className={labelCls}>Tagline</label><input className={inputCls} value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></div>
          <div><label className={labelCls}>Intro</label><textarea className={inputCls} rows={3} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} /></div>
          <ImageField label="Hero image" value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v, gallery: [v] })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Best time</label><input className={inputCls} value={editing.best_time} onChange={(e) => setEditing({ ...editing, best_time: e.target.value })} /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} /></div>
          </div>
          <div><label className={labelCls}>How to reach</label><input className={inputCls} value={editing.how_to_reach} onChange={(e) => setEditing({ ...editing, how_to_reach: e.target.value })} /></div>
          <div><label className={labelCls}>Where to stay</label><input className={inputCls} value={editing.where_to_stay} onChange={(e) => setEditing({ ...editing, where_to_stay: e.target.value })} /></div>
          <div><label className={labelCls}>Highlights (comma sep)</label><input className={inputCls} defaultValue={editing.highlights.join(", ")} onBlur={(e) => setEditing({ ...editing, highlights: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
          <div><label className={labelCls}>Experiences (comma sep)</label><input className={inputCls} defaultValue={editing.experiences.join(", ")} onBlur={(e) => setEditing({ ...editing, experiences: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
          <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
        </Modal>
      )}
    </Shell>
  );
}

// ---------------- Journal ----------------
export function JournalManager() {
  const empty = { title: "", category: "Destination Guides", excerpt: "", cover_image: "", content: "", author: "VacanzaGo", published: true };
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/journal").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    try {
      if (editing.slug) await api.put(`/journal/${editing.slug}`, editing);
      else await api.post("/journal", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (slug) => { if (!window.confirm("Delete post?")) return; await api.delete(`/journal/${slug}`); load(); };
  return (
    <Shell title="Journal" onAdd={() => setEditing({ ...empty })}>
      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.slug} className="flex items-center justify-between rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4">
            <div className="flex items-center gap-3">{p.cover_image && <img src={p.cover_image} alt="" className="h-10 w-14 rounded object-cover" />}<div><p className="font-medium text-[#2A1808]">{p.title}</p><p className="text-xs text-[#8A6B47]">{p.category}</p></div></div>
            <div className="flex items-center gap-1">
              <span className={`text-xs px-2 py-1 rounded-full ${p.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{p.published ? "Live" : "Draft"}</span>
              <button onClick={() => setEditing({ ...empty, ...p })} className="p-2 text-[#8A6B47] hover:text-[#F49E1A]"><Pencil size={15} /></button>
              <button onClick={() => del(p.slug)} className="p-2 text-[#8A6B47] hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} onSave={save} title={editing.slug ? "Edit post" : "New post"}>
          <div><label className={labelCls}>Title *</label><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Category</label>
              <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {["Destination Guides", "Travel Inspiration", "Practical Travel", "Pilgrimage", "Luxury Travel", "Itineraries", "Customer Stories", "Events & Celebration Inspiration"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Author</label><input className={inputCls} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
          </div>
          <div><label className={labelCls}>Excerpt</label><textarea className={inputCls} rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} /></div>
          <ImageField label="Cover image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} />
          <div><label className={labelCls}>Content (paragraphs separated by line breaks)</label><textarea className={inputCls} rows={8} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
        </Modal>
      )}
    </Shell>
  );
}

// ---------------- Stories ----------------
export function StoriesManager() {
  const empty = { family_name: "", title: "", destination: "", quote: "", story: "", image: "", trip_slug: "", rating: 5, published: true };
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/stories").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!editing.family_name) { toast.error("Name required"); return; }
    try {
      if (editing.id) await api.put(`/stories/${editing.id}`, editing);
      else await api.post("/stories", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete story?")) return; await api.delete(`/stories/${id}`); load(); };
  return (
    <Shell title="Customer Stories" onAdd={() => setEditing({ ...empty })}>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4">
            <div className="flex items-center gap-3">{s.image && <img src={s.image} alt="" className="h-10 w-14 rounded object-cover" />}<div><p className="font-medium text-[#2A1808]">{s.family_name}</p><p className="text-xs text-[#8A6B47]">{s.destination}</p></div></div>
            <div className="flex items-center gap-1">
              <span className={`text-xs px-2 py-1 rounded-full ${s.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{s.published ? "Live" : "Draft"}</span>
              <button onClick={() => setEditing({ ...empty, ...s })} className="p-2 text-[#8A6B47] hover:text-[#F49E1A]"><Pencil size={15} /></button>
              <button onClick={() => del(s.id)} className="p-2 text-[#8A6B47] hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Edit story" : "New story"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Family / traveller *</label><input className={inputCls} value={editing.family_name} onChange={(e) => setEditing({ ...editing, family_name: e.target.value })} /></div>
            <div><label className={labelCls}>Destination</label><input className={inputCls} value={editing.destination} onChange={(e) => setEditing({ ...editing, destination: e.target.value })} /></div>
          </div>
          <div><label className={labelCls}>Title</label><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
          <div><label className={labelCls}>Quote</label><input className={inputCls} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} /></div>
          <div><label className={labelCls}>Story</label><textarea className={inputCls} rows={4} value={editing.story} onChange={(e) => setEditing({ ...editing, story: e.target.value })} /></div>
          <ImageField label="Photo" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Trip slug (optional)</label><input className={inputCls} value={editing.trip_slug} onChange={(e) => setEditing({ ...editing, trip_slug: e.target.value })} /></div>
            <div><label className={labelCls}>Rating</label><input type="number" min={1} max={5} className={inputCls} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
        </Modal>
      )}
    </Shell>
  );
}

// ---------------- Newsletter ----------------
export function NewsletterManager() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/newsletter").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  return (
    <Shell title="Newsletter">
      <p className="text-sm text-[#8A6B47] mb-4">{subs.length} subscribers</p>
      <div className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] divide-y divide-[#553204]/8">
        {subs.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-[#2A1808]">{s.email}</span>
            <span className="text-xs text-[#8A6B47]">{new Date(s.created_at).toLocaleDateString("en-IN")}</span>
          </div>
        ))}
        {subs.length === 0 && <p className="p-4 text-[#8A6B47] text-sm">No subscribers yet.</p>}
      </div>
    </Shell>
  );
}

// ---------------- Site content ----------------
export function ContentManager() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setC(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/content", { data: c }); toast.success("Content saved — live on the site now"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!c) return <Shell title="Site Content"><p className="text-[#8A6B47]">Loading…</p></Shell>;
  const setChapter = (i, k, v) => { const hc = [...(c.hero_chapters || [])]; hc[i] = { ...hc[i], [k]: v }; setC({ ...c, hero_chapters: hc }); };
  const fields = [["home_promise_title", "Homepage promise — title"], ["home_promise_body", "Homepage promise — body"], ["home_featured_title", "Featured section title"], ["home_destinations_title", "Destinations section title"], ["events_headline", "Events headline"], ["final_cta_title", "Final CTA — title"], ["final_cta_body", "Final CTA — body"]];
  return (
    <Shell title="Site Content">
      <button onClick={save} data-testid="content-save" className="mb-6 inline-flex items-center gap-2 rounded-full btn-orange px-6 py-2.5 text-sm">Save changes</button>
      <h2 className="font-display text-2xl text-[#2A1808] mb-3">Hero scroll text (7 chapters)</h2>
      <div className="space-y-3 mb-8">
        {(c.hero_chapters || []).map((ch, i) => (
          <div key={i} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`content-chapter-${i}`}>
            <input className={inputCls} value={ch.eyebrow || ""} onChange={(e) => setChapter(i, "eyebrow", e.target.value)} placeholder="Eyebrow" />
            <input className={inputCls} value={ch.title || ""} onChange={(e) => setChapter(i, "title", e.target.value)} placeholder="Title (use \n for line break)" />
            <input className={inputCls} value={ch.sub || ""} onChange={(e) => setChapter(i, "sub", e.target.value)} placeholder="Subtitle" />
          </div>
        ))}
      </div>
      <h2 className="font-display text-2xl text-[#2A1808] mb-3">Homepage text</h2>
      <div className="space-y-4">
        {fields.map(([k, lbl]) => (
          <div key={k}>
            <label className={labelCls}>{lbl}</label>
            <textarea className={inputCls} rows={2} value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} />
          </div>
        ))}
      </div>
    </Shell>
  );
}

// ---------------- Google reviews ----------------
export function ReviewsManager() {
  const empty = { author: "", rating: 5, text: "", date: "", profile_photo: "", source: "Google", review_url: "", published: true };
  const [data, setData] = useState({ reviews: [], google_configured: false });
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/reviews").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!editing.author) { toast.error("Author required"); return; }
    try {
      if (editing.id) await api.put(`/reviews/${editing.id}`, editing);
      else await api.post("/reviews", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete review?")) return; await api.delete(`/reviews/${id}`); load(); };
  const sync = async () => {
    const { data: res } = await api.post("/reviews/google-sync");
    if (res.ok) { toast.success(`Synced ${res.synced} Google reviews`); load(); }
    else toast.error(res.message || "Not configured");
  };
  return (
    <Shell title="Google Reviews" onAdd={() => setEditing({ ...empty })}>
      {!data.google_configured && (
        <div className="mb-6 rounded-md border border-[#F49E1A]/40 bg-[#FFF7EC] p-4 text-sm text-[#553204]" data-testid="reviews-config-note">
          <b>Auto-sync not connected.</b> Add your real Google reviews below manually (they show with Google branding), or connect the API: set <code>GOOGLE_PLACES_API_KEY</code> and <code>GOOGLE_PLACE_ID</code> in backend env, then click Sync.
        </div>
      )}
      {data.google_configured && <button onClick={sync} className="mb-6 rounded-full border border-[#553204]/20 px-5 py-2 text-sm">Sync from Google</button>}
      <div className="space-y-2">
        {data.reviews.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-4">
            <div><p className="font-medium text-[#2A1808]">{r.author} · {r.rating}★</p><p className="text-xs text-[#8A6B47] line-clamp-2 max-w-xl">{r.text}</p></div>
            <div className="flex items-center gap-1">
              <span className={`text-xs px-2 py-1 rounded-full ${r.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{r.published ? "Live" : "Hidden"}</span>
              <button onClick={() => setEditing({ ...empty, ...r })} className="p-2 text-[#8A6B47] hover:text-[#F49E1A]"><Pencil size={15} /></button>
              <button onClick={() => del(r.id)} className="p-2 text-[#8A6B47] hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {data.reviews.length === 0 && <p className="text-[#8A6B47] text-sm">No reviews yet. Add your genuine Google reviews.</p>}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Edit review" : "Add Google review"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Author *</label><input className={inputCls} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
            <div><label className={labelCls}>Rating (1–5)</label><input type="number" min={1} max={5} className={inputCls} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} /></div>
          </div>
          <div><label className={labelCls}>Review text</label><textarea className={inputCls} rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Date (e.g. "2 weeks ago")</label><input className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
            <div><label className={labelCls}>Review URL (Google)</label><input className={inputCls} value={editing.review_url} onChange={(e) => setEditing({ ...editing, review_url: e.target.value })} /></div>
          </div>
          <ImageField label="Profile photo (optional)" value={editing.profile_photo} onChange={(v) => setEditing({ ...editing, profile_photo: v })} />
          <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
        </Modal>
      )}
    </Shell>
  );
}

function Modal({ title, children, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-[#FFF7EC] rounded-md w-full max-w-2xl my-8">
        <div className="sticky top-0 bg-[#FFF7EC] border-b border-[#553204]/10 px-6 py-4 flex items-center justify-between rounded-t-md z-10">
          <h2 className="font-display text-2xl text-[#2A1808]">{title}</h2>
          <button onClick={onClose}><X className="text-[#8A6B47]" /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="sticky bottom-0 bg-[#FFF7EC] border-t border-[#553204]/10 px-6 py-4 flex justify-end gap-3 rounded-b-md">
          <button onClick={onClose} className="rounded-full border border-[#553204]/20 px-5 py-2.5 text-sm text-[#553204]">Cancel</button>
          <button onClick={onSave} className="rounded-full btn-orange px-6 py-2.5 text-sm">Save</button>
        </div>
      </div>
    </div>
  );
}
